/** Get initials from a name or email for avatar display */
export const getInitials = (name?: string, email?: string) => {
	if (name) {
		return name
			.split(' ')
			.map(n => n[0])
			.join('')
			.toUpperCase()
			.slice(0, 2);
	}
	return email ? email[0].toUpperCase() : '?';
};

export const getSupportContactProfilePicSrc = (contact: {
	contactProfilePic?: string | null;
	profilePic?: string | null;
	contactId?: string | null;
	accountId?: string | null;
}): string | undefined => {
	const directProfilePic = contact.contactProfilePic || contact.profilePic;
	if (directProfilePic) return directProfilePic;
	if (!contact.contactId || !contact.accountId || !import.meta.env.VITE_API_URL) return undefined;

	const contactId = encodeURIComponent(contact.contactId);
	const accountId = encodeURIComponent(contact.accountId);
	return `${import.meta.env.VITE_API_URL}/contact/${contactId}/profilePic?accountId=${accountId}`;
};

export const getSupportUserProfilePicSrc = (user: {
	agentProfilePic?: string | null;
	authorProfilePic?: string | null;
	profilePic?: string | null;
	userId?: string | null;
	accountId?: string | null;
}): string | undefined => {
	const directProfilePic = user.agentProfilePic || user.authorProfilePic || user.profilePic;
	if (directProfilePic) return directProfilePic;
	if (!user.userId || !user.accountId || !import.meta.env.VITE_API_URL) return undefined;

	const userId = encodeURIComponent(user.userId);
	const accountId = encodeURIComponent(user.accountId);
	return `${import.meta.env.VITE_API_URL}/user/${userId}/profilePic?accountId=${accountId}`;
};

export const getUserReferenceName = (
	reference?: {
		name?: string;
		firstName?: string;
		lastName?: string;
		email?: string;
	},
	options: { includeEmail?: boolean } = {}
): string | undefined => {
	if (!reference) return undefined;
	const fullName = [reference.firstName, reference.lastName].filter(Boolean).join(' ').trim();
	return (
		reference.name?.trim() || fullName || (options.includeEmail !== false ? reference.email : undefined) || undefined
	);
};

export { formatFileSize } from '../fileSize';

/**
 * True when a react-query error is an authorization failure (HTTP 403).
 *
 * The support endpoints reject non-agents with a 403. WebServiceHelper preserves
 * the HTTP status on `systemCode`, so callers can distinguish "you are not an agent
 * here" from a generic failure and render a friendly state instead of a hard error.
 */
export const isForbiddenError = (error: unknown): boolean =>
	!!error && typeof error === 'object' && (error as { systemCode?: number }).systemCode === 403;

/**
 * True when a react-query error is an HTTP 404. Support surfaces use this to tell
 * "this capability does not exist here" (e.g. the GitHub integration endpoints are
 * absent from the target environment) apart from a genuine failure.
 */
export const isNotFoundError = (error: unknown): boolean =>
	!!error && typeof error === 'object' && (error as { systemCode?: number }).systemCode === 404;

/** Human-readable message from a react-query/API error, with a fallback. */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
	const message = error && typeof error === 'object' ? (error as { systemMessage?: string }).systemMessage : undefined;
	// Ignore the SDK's generic bodyless-response placeholder in favor of the caller's fallback.
	if (message && message.trim() && message !== 'Api did not return an operation result.') return message;
	// Genuine Error instances (thrown outside the API layer) carry their own message.
	if (error instanceof Error && error.message) return error.message;
	return fallback;
};

/**
 * react-query `retry` predicate for support endpoints: never retry client errors
 * (4xx — auth/validation failures won't succeed on retry), retry transient errors
 * up to 3 times.
 */
export const supportQueryRetry = (failureCount: number, error: unknown): boolean => {
	const code = error && typeof error === 'object' ? (error as { systemCode?: number }).systemCode : undefined;
	if (code != null && code >= 400 && code < 500) return false;
	return failureCount < 3;
};

/**
 * react-query `retry` predicate for actively-polling support queries. The poll itself
 * re-attempts every cycle, so stacking the standard 3 retries on top of it turns one
 * persistently-failing endpoint into a request storm (4 calls per cycle). Retry at most
 * once for transient errors; never retry client errors (4xx).
 */
export const supportPollingRetry = (failureCount: number, error: unknown): boolean =>
	supportQueryRetry(failureCount, error) && failureCount < 1;

/**
 * Wraps a support list query so a 403 (caller is not an agent for the mailbox)
 * resolves to an empty result instead of throwing. Non-agents then see an empty
 * list rather than a hard error — the correct UX for these secondary collections.
 * All other errors propagate.
 */
export const emptyOnForbidden = async <T>(load: () => Promise<T>, empty: T): Promise<T> => {
	try {
		return await load();
	} catch (error) {
		if (isForbiddenError(error)) return empty;
		throw error;
	}
};
